import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tv, Film, Plus, ListVideo, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { Badge } from '@/components/ds';
import { api } from '@/lib/api';
import { displayStatus } from '@/lib/types';
import type { Episodio, Demanda, Temporada } from '@/lib/types';

/**
 * Conteúdo "Programa TV/YouTube" — modelo playlist (§0.5).
 * Temporadas viram entidade própria (Programa livre → Temporada → Episódios).
 * Usado como aba dentro de Entregas e na página standalone.
 * Não renderiza PageHeader — o container/pai cuida disso.
 */
export function ProgramaTVSection() {
  const qc = useQueryClient();
  const temporadasQ = useQuery({ queryKey: ['temporadas'], queryFn: () => api.temporadas.list().then((r) => r.data) });
  const episodiosQ = useQuery({ queryKey: ['episodios'], queryFn: () => api.episodios.list().then((r) => r.data) });
  const demandasQ = useQuery({ queryKey: ['demandas'], queryFn: () => api.demandas.list().then((r) => r.data) });

  const temporadas = temporadasQ.data ?? [];
  const episodios = episodiosQ.data ?? [];
  const demandas = demandasQ.data ?? [];

  const [showForm, setShowForm] = useState(false);
  const [programa, setPrograma] = useState('Mundo Business');
  const [numero, setNumero] = useState('');
  const [anoSemestre, setAnoSemestre] = useState('');
  const [qtd, setQtd] = useState('12');
  const [statusTemp, setStatusTemp] = useState<'atual' | 'planejada' | 'encerrada'>('atual');

  const criar = useMutation({
    mutationFn: () => api.temporadas.create({
      programa: programa.trim(),
      numero: numero ? Number(numero) : undefined,
      anoSemestre: anoSemestre.trim() || undefined,
      status: statusTemp,
      qtdEpisodios: qtd ? Number(qtd) : 0,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['temporadas'] });
      qc.invalidateQueries({ queryKey: ['episodios'] });
      setShowForm(false);
      setNumero(''); setAnoSemestre(''); setQtd('12');
    },
  });

  const porEpisodio = useMemo(() => {
    const map = new Map<string, Demanda[]>();
    for (const d of demandas) {
      if (!d.episodioId) continue;
      (map.get(d.episodioId) ?? map.set(d.episodioId, []).get(d.episodioId)!).push(d);
    }
    return map;
  }, [demandas]);

  const episodiosDaTemporada = (t: Temporada): Episodio[] =>
    episodios
      .filter((e) => e.titulo && (e.temporadaId === t.id || (!e.temporadaId && e.temporada && e.temporada === t.temporada)))
      .slice()
      .sort((a, b) => Number(a.numero ?? 0) - Number(b.numero ?? 0));

  const legados = useMemo(() => {
    const semLink = episodios.filter((e) => e.titulo && !e.temporadaId);
    const grupos = new Map<string, Episodio[]>();
    for (const e of semLink) {
      const t = e.temporada?.trim() || 'Sem temporada';
      if (temporadas.some((tp) => tp.temporada === t)) continue;
      (grupos.get(t) ?? grupos.set(t, []).get(t)!).push(e);
    }
    return [...grupos.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [episodios, temporadas]);

  const loading = temporadasQ.isLoading || episodiosQ.isLoading || demandasQ.isLoading;
  const statusVariant = (s?: string) => (s === 'atual' ? 'green' : s === 'planejada' ? 'blue' : 'gray');

  function EpisodioRow({ ep }: { ep: Episodio }) {
    const materias = porEpisodio.get(ep.id) ?? [];
    const finalizadas = materias.filter((m) => m.status === 'Publicada').length;
    return (
      <div className="px-4 py-2.5 border-t border-border first:border-t-0">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-panel border border-border flex items-center justify-center text-[11px] font-bold text-blue shrink-0">
            {ep.numero || '·'}
          </span>
          <span className="text-[13px] text-text font-medium flex-1 truncate">{ep.titulo}</span>
          {materias.length > 0 && (
            <span className="text-[10px] text-muted">{finalizadas}/{materias.length} finalizadas</span>
          )}
        </div>
        {materias.length > 0 && (
          <div className="mt-2 ml-9 space-y-1">
            {materias.map((m) => (
              <div key={m.id} className="flex items-center gap-2">
                <span className="text-[12px] text-text-2 flex-1 truncate">{m.titulo}</span>
                {m.reelFatos && <Badge variant="purple" leading={<Film className="w-3 h-3" />}>Reel</Badge>}
                <span className={clsx('text-[10px] font-bold shrink-0',
                  m.status === 'Publicada' ? 'text-green' : m.status === 'Triagem' ? 'text-yellow' : 'text-blue')}>
                  {displayStatus(m.status)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Barra de ação */}
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] text-muted max-w-2xl">
          Cada temporada é uma "playlist": cadastre o programa e quantos episódios terá, e a plataforma gera os episódios.
          No card da matéria, basta selecionar programa → temporada → episódio.
        </p>
        <button className="btn-primary shrink-0" onClick={() => setShowForm((v) => !v)}>
          <Plus className="w-4 h-4" /> Nova temporada
        </button>
      </div>

      {/* Form nova temporada */}
      {showForm && (
        <div className="card !p-4 space-y-3">
          <div className="text-[11px] font-bold uppercase tracking-wide text-muted">Nova temporada</div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Programa</span>
              <input className="input w-full mt-1" list="programas-list" value={programa}
                onChange={(e) => setPrograma(e.target.value)} placeholder="Digite ou selecione…" />
              <datalist id="programas-list">
                {[...new Set([...temporadas.map((t) => t.programa).filter(Boolean) as string[], 'Mundo Business', 'Agro Business'])].map((p) => (
                  <option key={p} value={p} />
                ))}
              </datalist>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Nº da temporada</span>
              <input type="number" className="input w-full mt-1" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="13" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Ano / semestre</span>
              <input className="input w-full mt-1" value={anoSemestre} onChange={(e) => setAnoSemestre(e.target.value)} placeholder="2026/2" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Qtd. de episódios</span>
              <input type="number" className="input w-full mt-1" value={qtd} onChange={(e) => setQtd(e.target.value)} min={0} max={60} />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[12px] text-text-2">
              <span className="text-[10px] font-bold uppercase tracking-wide text-muted">Status</span>
              <select className="input" value={statusTemp} onChange={(e) => setStatusTemp(e.target.value as typeof statusTemp)}>
                <option value="atual">Atual</option>
                <option value="planejada">Planejada</option>
                <option value="encerrada">Encerrada</option>
              </select>
            </label>
            <div className="flex-1" />
            <button className="btn-secondary" onClick={() => setShowForm(false)} disabled={criar.isPending}>Cancelar</button>
            <button className="btn-primary" onClick={() => criar.mutate()} disabled={!programa.trim() || criar.isPending}>
              {criar.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListVideo className="w-4 h-4" />}
              Gerar temporada {qtd ? `+ ${qtd} episódios` : ''}
            </button>
          </div>
          {criar.isError && <div className="text-[12px] text-red">Não foi possível criar a temporada. Tente novamente.</div>}
        </div>
      )}

      {loading && <div className="text-sm text-muted">Carregando…</div>}

      {!loading && temporadas.length === 0 && legados.length === 0 && (
        <div className="text-sm text-muted">
          Nenhuma temporada ainda. Clique em "Nova temporada" para criar a primeira (ex.: Mundo Business · 13ª · 2026/2).
        </div>
      )}

      {/* Temporadas-entidade (playlists) */}
      {temporadas
        .slice()
        .sort((a, b) => (b.numero ?? 0) - (a.numero ?? 0))
        .map((t) => {
          const eps = episodiosDaTemporada(t);
          const totalMaterias = eps.reduce((acc, ep) => acc + (porEpisodio.get(ep.id)?.length ?? 0), 0);
          return (
            <section key={t.id} className="border border-border rounded-xl overflow-hidden bg-panel">
              <div className="flex items-center gap-3 px-4 py-3 bg-panel-2 border-b border-border">
                <Tv className="w-5 h-5 text-blue shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-bold text-text truncate">{t.temporada || `${t.programa} · ${t.numero ?? ''}ª`}</div>
                  <div className="text-[11px] text-muted">{t.programa} · {eps.length} episódios · {totalMaterias} matérias</div>
                </div>
                <Badge variant={statusVariant(t.status)}>{t.status ?? '—'}</Badge>
              </div>
              <div>
                {eps.length === 0
                  ? <div className="px-4 py-3 text-[12px] text-muted">Nenhum episódio nesta temporada.</div>
                  : eps.map((ep) => <EpisodioRow key={ep.id} ep={ep} />)}
              </div>
            </section>
          );
        })}

      {/* Episódios legados (texto solto, sem temporada-entidade) */}
      {legados.map(([temporada, eps]) => (
        <section key={`legado-${temporada}`} className="border border-dashed border-border rounded-xl overflow-hidden bg-panel/60">
          <div className="flex items-center gap-3 px-4 py-3 bg-panel-2 border-b border-border">
            <Tv className="w-5 h-5 text-muted shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-bold text-text-2 truncate">{temporada}</div>
              <div className="text-[11px] text-muted">{eps.length} episódios · vínculo legado (texto)</div>
            </div>
            <Badge variant="gray">legado</Badge>
          </div>
          <div>{eps.map((ep) => <EpisodioRow key={ep.id} ep={ep} />)}</div>
        </section>
      ))}
    </div>
  );
}
